import { memo } from "react";
import type { BackendScanResult } from "@/lib/intel/scan.functions";
import {
  User, MapPin, Building2, Newspaper, ShieldAlert, Globe, Wallet, GraduationCap,
  AtSign, ExternalLink, CheckCircle2, Link2, Boxes, Brain, Sparkles, AlertTriangle,
} from "lucide-react";

type Collector = BackendScanResult["raw"]["collectors"][number];

type BucketKey =
  | "identity" | "contacts" | "location" | "org" | "media"
  | "watchlists" | "infra" | "financial" | "academic" | "other";

const BUCKET_META: Record<BucketKey, { title: string; subtitle: string; icon: typeof User; tone: string }> = {
  identity:   { title: "Identity & aliases",      subtitle: "Who the subject appears to be online",            icon: User,          tone: "text-cyan" },
  contacts:   { title: "Contacts & accounts",     subtitle: "Email, handles, linked profiles & breaches",      icon: AtSign,        tone: "text-cyan" },
  location:   { title: "Likely location",         subtitle: "Geography, hosting, registered address signals",  icon: MapPin,        tone: "text-amber" },
  org:        { title: "Organisations & filings", subtitle: "Companies, registrations, regulatory filings",    icon: Building2,     tone: "text-cyan" },
  media:      { title: "Media & public mentions", subtitle: "News, press, social references, statements",      icon: Newspaper,     tone: "text-cyan" },
  watchlists: { title: "Watchlists & risk flags", subtitle: "Sanctions, wanted lists, abuse feeds",            icon: ShieldAlert,   tone: "text-danger" },
  infra:      { title: "Digital infrastructure",  subtitle: "DNS, certificates, hosting, headers, fingerprint", icon: Globe,        tone: "text-cyan" },
  financial:  { title: "Financial & blockchain",  subtitle: "Wallets, on-chain footprint, ENS, exchanges",     icon: Wallet,        tone: "text-amber" },
  academic:   { title: "Academic & research",     subtitle: "Publications, papers, scholarly references",      icon: GraduationCap, tone: "text-cyan" },
  other:      { title: "Other findings",          subtitle: "Additional collectors that returned data",        icon: Boxes,         tone: "text-cyan" },
};

const BUCKET_ORDER: BucketKey[] = ["identity", "contacts", "location", "org", "media", "watchlists", "infra", "financial", "academic", "other"];

// Explicit source label -> bucket map. Keep in sync with scan.functions.ts / collectors-extra.ts.
const SOURCE_BUCKET: Record<string, BucketKey> = {
  // identity / personas
  "Wikipedia": "identity",
  "Wikidata": "identity",
  "Wikipedia search": "identity",
  "OpenAlex authors": "identity",
  "Keybase": "identity",
  "Mastodon": "identity",
  "StackExchange": "identity",
  "DEV.to profile": "identity",
  "Hacker News profile": "identity",
  "Reddit profile": "identity",
  "GitHub profile": "identity",
  "GitLab profile": "identity",
  "Bitbucket profile": "identity",

  // contacts / accounts / breaches
  "Gravatar": "contacts",
  "OpenPGP keyserver": "contacts",
  "HIBP k-anon range": "contacts",
  "XposedOrNot breaches": "contacts",
  "Disposable-email heuristic": "contacts",
  "GitHub commits by email": "contacts",
  "Phone classification": "contacts",

  // location / network geography
  "IP geolocation": "location",
  "ipwho.is": "location",
  "Reverse DNS": "location",
  "Tor exit list": "location",
  "BGPView": "location",

  // organisations & filings
  "OpenCorporates": "org",
  "GLEIF (LEI)": "org",
  "SEC EDGAR": "org",
  "OpenSanctions screening": "watchlists",

  // media & mentions
  "GDELT news": "media",
  "Google News RSS": "media",
  "Reddit search": "media",
  "Hacker News Algolia": "media",
  "GitHub repositories": "media",
  "GitHub public search": "media",
  "DuckDuckGo Instant": "media",
  "Investigator dork pack": "media",
  "Common Crawl": "media",

  // watchlists / risk
  "OFAC SDN": "watchlists",
  "EU sanctions": "watchlists",
  "UK HMT sanctions": "watchlists",
  "FBI Wanted": "watchlists",
  "URLhaus": "watchlists",
  "PhishStats": "watchlists",
  "OTX reputation": "watchlists",
  "OTX passive DNS": "watchlists",
  "GreyNoise community": "watchlists",
  "Shodan InternetDB": "watchlists",

  // infrastructure
  "HTTP headers": "infra",
  "Certificate Transparency": "infra",
  "CertSpotter": "infra",
  "Wayback Machine": "infra",
  "Wayback CDX": "infra",
  "urlscan.io": "infra",
  "Mozilla Observatory": "infra",
  "Tech fingerprint": "infra",
  "SPF posture": "infra",
  "Favicon hash": "infra",

  // financial / blockchain
  "Ethplorer wallet": "financial",
  "ENS reverse": "financial",
  "Blockstream BTC": "financial",
  "Etherscan link": "financial",

  // academic
  "OpenAlex (academic)": "academic",
  "Crossref": "academic",
};

// Hide internal/orchestration sources from the profile.
const HIDDEN_SOURCES = new Set<string>(["CARIO backend", "Collector orchestrator", "Scoring engine", "Investigator pivot links"]);

function bucketFor(source: string): BucketKey {
  if (SOURCE_BUCKET[source]) return SOURCE_BUCKET[source];
  const s = source.toLowerCase();
  if (/sanction|ofac|wanted|urlhaus|phish|greynoise|shodan|otx|abuse/.test(s)) return "watchlists";
  if (/dns|cert|http|wayback|urlscan|observatory|spf|favicon|fingerprint|robots|sitemap|rdap/.test(s)) return "infra";
  if (/news|gdelt|reddit|hacker news|github|duckduckgo|dork|common crawl|mention/.test(s)) return "media";
  if (/geo|ipwho|tor|bgp|reverse dns/.test(s)) return "location";
  if (/corporat|gleif|edgar|filing/.test(s)) return "org";
  if (/ethereum|eth|btc|bitcoin|wallet|ens|blockstream|etherscan/.test(s)) return "financial";
  if (/scholar|openalex|crossref|academic/.test(s)) return "academic";
  if (/email|pgp|breach|hibp|xposed|phone|gravatar/.test(s)) return "contacts";
  if (/profile|wikipedia|wikidata|keybase|mastodon|stackexchange/.test(s)) return "identity";
  return "other";
}

function bucketCollectors(collectors: Collector[]) {
  const map = new Map<BucketKey, Collector[]>();
  for (const c of collectors) {
    if (HIDDEN_SOURCES.has(c.source)) continue;
    const key = bucketFor(c.source);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(c);
  }
  return map;
}

function normalizeForMatch(value: unknown): string {
  let text: string;
  if (value == null) text = "";
  else if (["string", "number", "boolean"].includes(typeof value)) text = String(value);
  else { try { text = JSON.stringify(value); } catch { text = String(value); } }
  return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function exactPersonMatch(target: string, value: unknown): boolean {
  const parts = normalizeForMatch(target).split(/\s+/).filter((p) => p.length >= 2);
  if (parts.length < 2) return normalizeForMatch(value).includes(parts[0] ?? "");
  const text = normalizeForMatch(value);
  if (text.replace(/\s+/g, "").includes(parts.join(""))) return true;
  return parts.every((part) => new RegExp(`(^|\\s)${part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}($|\\s)`).test(text));
}

function derivedPersonHandles(target: string): string[] {
  const parts = normalizeForMatch(target).split(/\s+/).filter(Boolean);
  if (!parts.length) return [];
  const first = parts[0]!;
  const last = parts[parts.length - 1] ?? "";
  const handles = new Set([parts.join("")]);
  if (first && last && first !== last) {
    handles.add(`${first}.${last}`);
    handles.add(`${first}_${last}`);
    handles.add(`${first}-${last}`);
    handles.add(`${last}${first}`);
    handles.add(`${last}.${first}`);
  }
  return [...handles];
}

function strictHandleMatch(target: string, value: unknown): boolean {
  const text = normalizeForMatch(value).replace(/\s+/g, "");
  return !!text && derivedPersonHandles(target).some((handle) => text.includes(handle.replace(/[^a-z0-9]/g, "")));
}

function strictPersonCollectors(target: string, collectors: Collector[]): Collector[] {
  return collectors.map((c) => {
    if (c.status !== "ok") return c;
    const d = (c.data ?? {}) as Record<string, unknown>;
    const miss = (summary: string): Collector => ({ ...c, status: "miss", summary });
    const filterArray = (key: string) => {
      const items = Array.isArray(d[key]) ? d[key].filter((item) => exactPersonMatch(target, item)) : [];
      return items.length ? { ...c, summary: `${items.length} exact target match${items.length === 1 ? "" : "es"}`, data: { ...d, [key]: items } as Collector["data"] } : null;
    };
    if (["Common Crawl", "Investigator dork pack"].includes(c.source)) return miss("Search pivots hidden until an exact-name result is verified");
    if (["Wikipedia", "DuckDuckGo Instant", "StackExchange", "Mastodon"].includes(c.source)) return exactPersonMatch(target, c) ? c : miss("Result did not contain the exact target name");
    if (["GitHub profile", "GitLab profile", "Bitbucket profile", "Keybase", "DEV.to profile", "Hacker News profile", "Reddit profile"].includes(c.source)) {
      const login = d.login ?? d.username ?? d.id ?? d.name ?? (d.basics as Record<string, unknown> | undefined)?.username;
      return exactPersonMatch(target, c) || strictHandleMatch(target, login) ? c : miss("Profile exists, but the public name/handle does not strictly match the target");
    }
    if (c.source === "Wikipedia search") return filterArray("results") ?? miss("No exact-name Wikipedia match returned");
    if (c.source === "Wikidata") return filterArray("hits") ?? miss("No exact-name Wikidata entity returned");
    if (c.source === "OpenAlex authors") return filterArray("items") ?? miss("No exact-name academic author match returned");
    if (c.source === "OpenAlex (academic)") return filterArray("works") ?? miss("No scholarly work contained the exact target name");
    if (c.source === "Crossref") return filterArray("items") ?? miss("Crossref results did not contain the exact target name");
    if (c.source === "GDELT news") return filterArray("articles") ?? miss("News results did not contain the exact target name");
    if (c.source === "Google News RSS" || c.source === "Reddit search" || c.source === "GitHub repositories" || c.source === "GitHub public search") return filterArray("items") ?? miss("Results did not strictly match the target name");
    if (c.source === "Hacker News Algolia") return filterArray("hits") ?? miss("HN results did not contain the exact target name");
    return c;
  });
}

function SubjectProfileImpl({ result }: { result: BackendScanResult | null }) {
  if (!result) {
    return (
      <div className="glass rounded-lg p-6 text-center text-sm text-muted-foreground">
        Run an investigation to see a clear subject profile — identity, contacts, location, media mentions, and risk flags will be grouped here.
      </div>
    );
  }
  const collectors = result.targetType === "person" ? strictPersonCollectors(result.target, result.raw.collectors ?? []) : (result.raw.collectors ?? []);
  const map = bucketCollectors(collectors);
  const sections = BUCKET_ORDER
    .map((key) => ({ key, ...BUCKET_META[key], items: map.get(key) ?? [] }))
    .filter((b) => b.items.length > 0);

  if (sections.length === 0) {
    return (
      <div className="space-y-3">
        <HeaderCard result={result} collectors={collectors} />
        <div className="glass rounded-lg p-6 text-center text-sm text-muted-foreground">
          The backend persisted the scan but no detailed collector data is available in this view yet. Re-run the scan to repopulate the structured profile.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <HeaderCard result={result} collectors={collectors} />
      <GrokAnalysisCard result={result} />
      <div className="grid gap-3 lg:grid-cols-2">
        {sections.map((s) => (
          <ProfileSection key={s.key} title={s.title} subtitle={s.subtitle} icon={s.icon} tone={s.tone} items={s.items} />
        ))}
      </div>
    </div>
  );
}

type GrokAnalysis = {
  narrative?: string;
  identity_summary?: string;
  key_findings?: Array<{ title?: string; detail?: string; severity?: string; sources?: string[] }>;
  entities?: Array<{ name?: string; type?: string; confidence?: number; aliases?: string[] }>;
  risk_signals?: Array<{ label?: string; severity?: string; rationale?: string }>;
  conflicts?: Array<{ claim?: string; sources?: string[]; note?: string }>;
  pivots?: Array<{ target?: string; type?: string; reason?: string }>;
  citations?: string[];
  model?: string;
};

function GrokAnalysisCard({ result }: { result: BackendScanResult }) {
  const raw = (result.raw ?? {}) as Record<string, unknown>;
  const analysis = raw.grokAnalysis as GrokAnalysis | null | undefined;
  const error = typeof raw.grokError === "string" ? (raw.grokError as string) : null;
  if (!analysis && !error) return null;
  if (error && !analysis) {
    return (
      <div className="glass rounded-lg p-4 border border-amber/30">
        <div className="flex items-center gap-2 text-amber">
          <Brain className="h-4 w-4" />
          <div className="font-mono text-[10px] uppercase tracking-[0.3em]">Grok intelligence</div>
        </div>
        <div className="mt-2 text-sm text-muted-foreground">Grok analysis unavailable: {error}</div>
      </div>
    );
  }
  const a = analysis as GrokAnalysis;
  return (
    <div className="glass rounded-lg p-4 ring-1 ring-cyan/20">
      <div className="flex items-center gap-2">
        <div className="grid h-9 w-9 place-items-center rounded-md bg-cyan/10 ring-1 ring-cyan/30">
          <Sparkles className="h-4 w-4 text-cyan" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base leading-tight">Grok intelligence synthesis</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">{a.model ?? "grok"} · live search</div>
        </div>
      </div>
      {a.identity_summary && <div className="mt-3 text-sm leading-snug">{a.identity_summary}</div>}
      {a.narrative && <div className="mt-2 text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">{a.narrative}</div>}

      {Array.isArray(a.key_findings) && a.key_findings.length > 0 && (
        <div className="mt-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Key findings</div>
          <div className="space-y-2">
            {a.key_findings.slice(0, 10).map((f, i) => (
              <div key={i} className="rounded-md border border-panel-border bg-background/40 p-2.5">
                <div className="flex items-center gap-2">
                  <span className={`font-mono text-[9px] uppercase px-1.5 py-0.5 rounded ${sevTone(f.severity)}`}>{f.severity ?? "info"}</span>
                  <div className="text-sm font-medium truncate">{f.title}</div>
                </div>
                {f.detail && <div className="mt-1 text-xs text-muted-foreground">{f.detail}</div>}
                {Array.isArray(f.sources) && f.sources.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {f.sources.slice(0, 5).map((s, j) => (
                      <a key={j} href={s} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full border border-panel-border bg-panel/40 px-2 py-0.5 text-[10px] hover:border-cyan/40 hover:text-cyan">
                        <Link2 className="h-3 w-3" />
                        <span className="truncate max-w-[200px]">{safeHost(s)}</span>
                        <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(a.risk_signals) && a.risk_signals.length > 0 && (
        <div className="mt-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Risk signals</div>
          <div className="grid gap-2 sm:grid-cols-2">
            {a.risk_signals.map((r, i) => (
              <div key={i} className="rounded-md border border-panel-border bg-background/40 p-2 flex items-start gap-2">
                <AlertTriangle className={`h-3.5 w-3.5 shrink-0 ${r.severity === "critical" || r.severity === "high" ? "text-danger" : "text-amber"}`} />
                <div className="min-w-0">
                  <div className="text-xs font-medium">{r.label}</div>
                  {r.rationale && <div className="text-[11px] text-muted-foreground">{r.rationale}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(a.pivots) && a.pivots.length > 0 && (
        <div className="mt-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Suggested pivots</div>
          <div className="flex flex-wrap gap-1.5">
            {a.pivots.slice(0, 12).map((p, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-full border border-cyan/30 bg-cyan/5 px-2 py-0.5 text-[11px]" title={p.reason}>
                <span className="font-mono text-[9px] uppercase text-cyan">{p.type}</span>
                <span className="truncate max-w-[200px]">{p.target}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(a.citations) && a.citations.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground">
            {a.citations.length} live-search sources
          </summary>
          <div className="mt-2 space-y-1">
            {a.citations.slice(0, 30).map((c, i) => (
              <a key={i} href={c} target="_blank" rel="noopener noreferrer" className="block truncate text-[11px] text-cyan hover:underline">
                {i + 1}. {c}
              </a>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function sevTone(sev?: string): string {
  switch (sev) {
    case "critical": return "bg-danger/20 text-danger";
    case "high": return "bg-danger/15 text-danger";
    case "medium": return "bg-amber/15 text-amber";
    case "low": return "bg-cyan/15 text-cyan";
    default: return "bg-muted/40 text-muted-foreground";
  }
}

function safeHost(url: string): string {
  try { return new URL(url).hostname; } catch { return url.slice(0, 40); }
}

function HeaderCard({ result, collectors }: { result: BackendScanResult; collectors: Collector[] }) {
  const ok = collectors.filter((c) => c.status === "ok").length;
  return (
    <div className="glass rounded-lg p-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Subject profile · {result.targetType}</div>
          <h2 className="mt-1 font-display text-2xl truncate">{result.target}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {ok} out of {collectors.length || result.sourceCount} public sources returned strict target-matched data. Below is what we found, grouped by what it tells you.
          </p>
        </div>
        <div className="flex gap-2">
          <Pill
            label="Trust"
            value={result.trustScore === null || result.trustScore === undefined ? "No score" : `${result.trustScore}/100`}
            tone={
              result.riskScore === null || result.riskScore === undefined
                ? "cyan"
                : result.riskScore > 70
                ? "danger"
                : result.riskScore > 45
                ? "amber"
                : "success"
            }
          />
          <Pill label="Confidence" value={result.confidence === null || result.confidence === undefined ? "n/a" : `${result.confidence}%`} tone="cyan" />
        </div>
      </div>
      <ScoreBreakdownStrip result={result} />
    </div>
  );
}

function ScoreBreakdownStrip({ result }: { result: BackendScanResult }) {
  const bd = result.scoreBreakdown;
  const scored = (bd?.categories ?? []).filter((c) => c.points > 0);
  return (
    <div className="mt-4 border-t border-panel-border pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
          How this score was calculated
        </div>
        <a href="/guide#scoring" className="font-mono text-[10px] uppercase tracking-wide text-cyan underline">
          Scoring methodology
        </a>
      </div>
      {scored.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">
          No adverse evidence was returned by any collector — the trust score stays at the clean baseline. Points are only
          added when a source returns an actual hit (sanctions match, malicious URL, breach record, adverse coverage).
        </p>
      ) : (
        <ul className="mt-2 space-y-2">
          {scored.map((c) => (
            <li key={c.key} className="rounded-md border border-panel-border bg-panel/40 p-2.5">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm">{c.label}</span>
                <span className="font-mono text-xs text-danger">−{c.points} trust</span>
              </div>
              <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-panel-border">
                <div className="h-full rounded-full bg-danger/70" style={{ width: `${Math.min(100, (c.points / c.cap) * 100)}%` }} />
              </div>
              <ul className="mt-1.5 space-y-0.5">
                {c.evidence.map((e, i) => (
                  <li key={i} className="text-xs text-muted-foreground">· {e}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Pill({ label, value, tone }: { label: string; value: string; tone: "cyan" | "amber" | "danger" | "success" }) {
  const cls = tone === "danger" ? "text-danger border-danger/40 bg-danger/10"
    : tone === "amber" ? "text-amber border-amber/40 bg-amber/10"
    : tone === "success" ? "text-success border-success/40 bg-success/10"
    : "text-cyan border-cyan/40 bg-cyan/10";
  return (
    <div className={`rounded-md border px-3 py-1.5 ${cls}`}>
      <div className="font-mono text-[9px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="font-display text-base leading-tight">{value}</div>
    </div>
  );
}

function ProfileSection({
  title, subtitle, icon: Icon, tone, items,
}: {
  title: string; subtitle: string; icon: typeof User; tone: string; items: Collector[];
}) {
  const hits = items.filter((i) => i.status === "ok");
  const misses = items.filter((i) => i.status !== "ok");
  return (
    <div className="glass rounded-lg p-4">
      <div className="flex items-start gap-3 pb-3 border-b border-panel-border">
        <div className="grid h-9 w-9 place-items-center rounded-md bg-cyan/10 ring-1 ring-cyan/30">
          <Icon className={`h-4 w-4 ${tone}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base leading-tight">{title}</div>
          <div className="text-xs text-muted-foreground">{subtitle}</div>
        </div>
        <div className="font-mono text-[10px] text-muted-foreground whitespace-nowrap">
          {hits.length} hit{hits.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {hits.length === 0 && (
          <div className="text-xs text-muted-foreground italic">No data returned from the public sources in this category.</div>
        )}
        {hits.map((c, i) => (
          <CollectorItem key={`h-${i}`} c={c} />
        ))}
        {misses.length > 0 && hits.length > 0 && (
          <details className="mt-2">
            <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground">
              {misses.length} source{misses.length === 1 ? "" : "s"} checked, no data
            </summary>
            <div className="mt-2 space-y-1 pl-2 border-l border-panel-border">
              {misses.map((c, i) => (
                <div key={`m-${i}`} className="text-xs text-muted-foreground">
                  <span className="font-mono text-[10px]">{c.source}</span> · {c.summary}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

function CollectorItem({ c }: { c: Collector }) {
  const links = extractLinks(c);
  return (
    <div className="rounded-md border border-panel-border bg-background/40 p-2.5">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" />
        <div className="font-mono text-[10px] uppercase tracking-widest text-cyan truncate">{c.source}</div>
      </div>
      <div className="mt-1 text-sm leading-snug">{c.summary}</div>
      {links.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {links.slice(0, 6).map((l, i) => (
            <a key={i} href={l.url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-full border border-panel-border bg-panel/40 px-2 py-0.5 text-[11px] hover:border-cyan/40 hover:text-cyan">
              <Link2 className="h-3 w-3" />
              <span className="truncate max-w-[180px]">{l.label}</span>
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function extractLinks(c: Collector): { url: string; label: string }[] {
  const out: { url: string; label: string }[] = [];
  const seen = new Set<string>();
  const push = (url?: unknown, label?: string) => {
    if (typeof url !== "string") return;
    if (!/^https?:\/\//i.test(url)) return;
    if (seen.has(url)) return;
    seen.add(url);
    out.push({ url, label: label || new URL(url).hostname });
  };
  const pushDoi = (doi: unknown, label?: string) => {
    if (typeof doi !== "string" || !doi) return;
    const clean = doi.replace(/^https?:\/\/(dx\.)?doi\.org\//i, "");
    push(`https://doi.org/${clean}`, label || `doi:${clean}`);
  };
  const walk = (v: unknown) => {
    if (!v) return;
    if (typeof v === "string") {
      if (/^https?:\/\//i.test(v)) push(v);
      return;
    }
    if (Array.isArray(v)) { v.forEach(walk); return; }
    if (typeof v === "object") {
      const o = v as Record<string, unknown>;
      const label = (o.title as string) || (o.name as string) || (o.login as string) || undefined;
      if (typeof o.url === "string") push(o.url, label);
      if (typeof o.html_url === "string") push(o.html_url, label);
      if (typeof o.link === "string") push(o.link, label);
      if (typeof o.permalink === "string") push(o.permalink, label);
      if (typeof o.href === "string") push(o.href, label);
      if (o.doi) pushDoi(o.doi, label);
      if (o.DOI) pushDoi(o.DOI, label);
      Object.values(o).forEach(walk);
    }
  };
  walk(c.data);
  // Source-specific fallback links when raw data lacks URLs.
  if (out.length === 0) {
    const s = c.source;
    const d = (c.data ?? {}) as Record<string, unknown>;
    if (s === "Common Crawl" && typeof d.collection === "string") {
      push(`https://index.commoncrawl.org/${d.collection}-index`, `Browse ${d.collection}`);
    }
  }
  return out;
}

/** Memoised: these panels render large evidence sets and must not re-render on every keystroke. */
export const SubjectProfile = memo(SubjectProfileImpl);
